import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress, Divider } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { COLUMN_CATALOG_MAP } from '../../lib/constants'
import { effectiveFormat } from '../../lib/effectiveFormat'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { usePortfolioData } from '../PortfolioDataProvider'
import { useUtilityPanel } from '../UtilityPanelProvider'
import { buildLevelRequest, buildRowRequest } from './allocation/buildAllocationRequest'
import { useAllocationQuery } from './allocation/useAllocationQuery'
import { computeSlices } from './allocation/computeSlices'
import { loadChartType, saveChartType } from './allocation/chartTypePreference'
import MeasurePicker from './allocation/MeasurePicker'
import ChartTypeToggle from './allocation/ChartTypeToggle'
import AllocationDonut from './allocation/AllocationDonut'
import AllocationBarChart from './allocation/AllocationBarChart'
import AllocationTreeMap from './allocation/AllocationTreeMap'
import AllocationDetailTable from './allocation/AllocationDetailTable'

// Chart-type dispatch. Keep keys in sync with CHART_TYPES in
// chartTypePreference.js -- the preference module validates incoming
// values against that list, so a typo here would silently fall back
// to donut.
const CHART_COMPONENTS = {
  donut: AllocationDonut,
  bar: AllocationBarChart,
  treemap: AllocationTreeMap
}

const useStyles = makeStyles(() => ({
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 14,
    overflow: 'auto'
  },
  loading: {
    flex: 1,
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  error: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13
  },
  empty: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  warningChip: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    padding: '4px 8px',
    borderRadius: 4,
    background: 'var(--color-warning-050, #fff8e1)',
    color: 'var(--color-warning-700, #b26a00)',
    border: '1px solid var(--color-warning-200, #ffe082)'
  }
}))

// Identifies columns that make sense in a pie chart. v1 is intentionally
// narrow -- only material currency columns. Derived currency columns
// (e.g. mvChange) are conceptually deltas, not magnitudes, and window
// columns are percentages-of-something; allowing them in v1 would
// invite confusing charts. Future flag on the catalog entry could
// loosen this without changing the picker plumbing.
function isPieEligible (col) {
  const catalog = COLUMN_CATALOG_MAP[col.columnType]
  if (!catalog) return false
  if (catalog.kind && catalog.kind !== 'material') return false
  return catalog.measureKind === 'currency'
}

function deriveMeasureOptions (config, columnDefinitions) {
  // Join config.columns (carries columnType) with columnDefinitions
  // (carries header / format / measureKind). The grid stores values
  // on each row keyed by `col.id`, which matches accessorKey.
  const defsById = new Map((columnDefinitions || []).map(d => [d.accessorKey, d]))
  const out = []
  for (const col of config.columns || []) {
    if (!isPieEligible(col)) continue
    const def = defsById.get(col.id)
    if (!def) continue
    out.push({
      id: col.id,
      label: def.header,
      format: def.format,
      measureKind: def.measureKind
    })
  }
  return out
}

export default function AllocationTool ({ payload }) {
  const classes = useStyles()
  const { formatter: fmt } = useFormattingContext()
  const { userId } = useAppContext()
  const {
    config,
    asOfDate,
    columnDefinitions,
    groupings,
    prefs,
    clientId,
    entity,
    entityIds
  } = usePortfolioData()
  const { setHeaderMeta, registerToolActions } = useUtilityPanel()

  // -- chart type --
  // Per-user choice, persisted to localStorage. Defaults to donut on
  // first use and for users without a stored preference.
  const [chartType, setChartTypeState] = useState(() => loadChartType(userId))
  const handleChartTypeChange = useCallback((next) => {
    setChartTypeState(next)
    saveChartType(userId, next)
  }, [userId])

  // -- request synthesis --
  // payload.scope: 'level' | 'row'
  // payload.depth: index into groupings (for both scopes -- for 'row'
  //   it's the CHILD depth, set by the caller)
  // payload.groupingLabel: display label for the panel header
  // payload.row: the source row (only for 'row' scope)
  //
  // Builders return { request, unwrap }. For filtered-child requests
  // (non-entity-scopable row scopes), unwrap carries the row identity
  // so the hook can dive into the wrapper grouping's children -- same
  // unwrap fetchRowChildren applies for lazy expand.
  const built = useMemo(() => {
    if (!payload || !config || !asOfDate || !entity || !entityIds) return null
    if (payload.scope === 'level') {
      return buildLevelRequest({ config, asOfDate, entity, entityIds, depth: payload.depth })
    }
    if (payload.scope === 'row') {
      return buildRowRequest({ config, asOfDate, entity, entityIds, row: payload.row })
    }
    return null
  }, [payload, config, asOfDate, entity, entityIds])

  const request = built?.request || null
  const unwrap = built?.unwrap || null

  const globalDateConfig = useMemo(() => ({
    dateMode: config.dateMode,
    quickDate: config.quickDate,
    startDate: config.startDate,
    endDate: config.endDate
  }), [config.dateMode, config.quickDate, config.startDate, config.endDate])

  const { items, isFetching, error } = useAllocationQuery({
    request,
    clientId,
    columnInstances: config.columns,
    globalDateConfig,
    entity,
    entityIds,
    unwrap
  })

  // -- measure state --
  const measureOptions = useMemo(
    () => deriveMeasureOptions(config, columnDefinitions),
    [config, columnDefinitions]
  )
  const defaultMeasureId = measureOptions[0]?.id || null
  const [measureId, setMeasureId] = useState(defaultMeasureId)

  // Snap measureId to a valid option during render rather than via a
  // post-paint effect. If the config changes and the previously-
  // selected measure goes away (or there was no selection), fall back
  // to the new first eligible. Render-phase setState bails out via
  // Object.is when the chosen value already matches state, so the
  // steady-state case is free; the one-off correction is a single
  // re-render with no wasted commit.
  //
  // See React docs "Adjusting some state when a prop changes":
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (measureOptions.length > 0) {
    const stillValid = measureId && measureOptions.some(o => o.id === measureId)
    if (!stillValid && defaultMeasureId !== measureId) {
      setMeasureId(defaultMeasureId)
    }
  }

  const measureMeta = measureOptions.find(o => o.id === measureId) || null
  const valueFormat = useMemo(
    () => measureMeta ? effectiveFormat(measureMeta.format, measureMeta.measureKind, prefs) : null,
    [measureMeta, prefs]
  )

  // -- slice derivation --
  const slicing = useMemo(
    () => computeSlices(items, measureId),
    [items, measureId]
  )
  const { slices, totalRaw, hasNegatives, skippedZero } = slicing

  // -- header metadata --
  useEffect(() => {
    const groupingLabel = payload?.groupingLabel || groupings?.[payload?.depth] || 'Allocation'
    const parentName = payload?.scope === 'row' ? payload?.row?.name : null
    const title = parentName ? `${groupingLabel} in ${parentName}` : groupingLabel
    let subjectLine
    if (isFetching) {
      subjectLine = 'Loading…'
    } else if (error) {
      subjectLine = 'Failed to load'
    } else if (!measureMeta) {
      subjectLine = asOfDate ? `as of ${asOfDate}` : null
    } else {
      // Use totalRaw (sum of visible slices) so the displayed total
      // matches the chart for both scopes. For 'level' it's the
      // household total; for 'row' it's the parent row's value
      // (children sum to parent). transformResponse's top-level
      // `totalRow` is always the household, which would be misleading
      // for row scope.
      const sumText = fmt(totalRaw, valueFormat)
      const n = slices.length
      const itemText = `${n} item${n === 1 ? '' : 's'}`
      subjectLine = asOfDate ? `${itemText} · ${sumText} · as of ${asOfDate}` : `${itemText} · ${sumText}`
    }
    setHeaderMeta({ eyebrow: 'Allocation', title, subjectLine })
  }, [payload, groupings, isFetching, error, measureMeta, valueFormat, totalRaw, slices.length, asOfDate, fmt, setHeaderMeta])

  // -- toolbar actions --
  useEffect(() => {
    if (measureOptions.length === 0) {
      registerToolActions(null)
      return undefined
    }
    registerToolActions(
      <>
        <ChartTypeToggle value={chartType} onChange={handleChartTypeChange} />
        <Divider orientation='vertical' flexItem style={{ margin: '0 6px' }} />
        <MeasurePicker
          measureOptions={measureOptions}
          value={measureId}
          onChange={setMeasureId}
        />
      </>
    )
    return () => registerToolActions(null)
  }, [measureOptions, measureId, chartType, handleChartTypeChange, registerToolActions])

  // -- render --
  if (measureOptions.length === 0) {
    return (
      <div className={classes.empty}>
        Add a currency column to your view to chart its allocation.
      </div>
    )
  }

  if (!request) {
    return (
      <div className={classes.empty}>
        No deeper grouping level available to chart.
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.error}>
        <span>Could not load allocation.</span>
        <span>{error?.message || 'Unknown error'}</span>
      </div>
    )
  }

  if (isFetching && slices.length === 0) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={18} />
        <span>Loading allocation…</span>
      </div>
    )
  }

  if (slices.length === 0) {
    return (
      <div className={classes.empty}>
        No values to chart for this measure.
      </div>
    )
  }

  return (
    <div className={classes.body}>
      {hasNegatives && (
        <div className={classes.warningChip}>
          Negative values are charted by absolute size; signed values appear in the table below.
        </div>
      )}
      {skippedZero > 0 && !hasNegatives && (
        <div className={classes.warningChip}>
          {skippedZero} row{skippedZero === 1 ? '' : 's'} omitted (no value). Listed below the chart.
        </div>
      )}
      {React.createElement(CHART_COMPONENTS[chartType] || AllocationDonut, {
        slices,
        valueFormat,
        height: 340
      })}
      <AllocationDetailTable
        slices={slices}
        items={items}
        measureId={measureId}
        totalRaw={totalRaw}
        valueFormat={valueFormat}
      />
    </div>
  )
}

AllocationTool.propTypes = {
  payload: PropTypes.shape({
    scope: PropTypes.oneOf(['level', 'row']).isRequired,
    depth: PropTypes.number,
    groupingLabel: PropTypes.string,
    row: PropTypes.object
  })
}
